import React, { useState, useContext, useEffect } from 'react'
import { SpinnerComponent } from 'react-element-spinner'
import { Button, Card, CardFooter, CardHeader, Col, Container, Label, Row, Table } from 'reactstrap'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker'
import dayjs from 'dayjs'
import "react-datepicker/dist/react-datepicker.css"

import Paginator from '../../components/Paginator/Paginator'
import PageTitle from '../../components/PageTitles/PageTitle'
import { SessionContext } from '../../contexts/SessionProvider'
import ReportingService from '../../services/ReportingService'
import SiteService from "../../services/SiteService";

const ListingReport = () =>
{
	const { t } = useTranslation()
	const [isBusy, setIsBusy] = useState(false)
	const sessionContext = useContext(SessionContext)
	const [reportRequest, setReportRequest] = React.useState(	{ pageIndex: 0, pageSize: 15, reportStartDate: '', reportEndDate: '' })
	const [pagingResult, setPagingResult] = React.useState({ pageCollection: [], totalRecords: 0 })
	
	useEffect(() =>
	{
		setReportRequest((previousReportRequest) =>
		{
			let currentReportRequest = { ...previousReportRequest }
			
			currentReportRequest.reportStartDate = new Date()
			currentReportRequest.reportStartDate.setMonth(currentReportRequest.reportStartDate.getMonth() - 1)
			
			currentReportRequest.reportEndDate = new Date()
			
			retrieveReport(currentReportRequest)
			
			return currentReportRequest
		})
		
	}, [])
	
	const retrieveReport = (request) =>
	{
		setIsBusy(true)
		ReportingService
			.getListingReport(dayjs(request.reportStartDate).format('MMM DD, YYYY'), dayjs(request.reportEndDate).format('MMM DD, YYYY'), sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setPagingResult((previousPagingResult) =>
				{
					let currentPagingResult = { ...previousPagingResult }
					currentPagingResult.pageCollection = result.data.actionResult
					currentPagingResult.totalRecords = result.data.actionResult.length
					
					return currentPagingResult
				})
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleGetReport = () =>
	{
		setReportRequest((previousReportRequest) => ({ ...previousReportRequest, pageIndex: 0 }))
		retrieveReport(reportRequest)
	}
	
	const handlePageChange = (pageIndex) =>
	{
		setReportRequest((previousReportRequest) => ({ ...previousReportRequest, pageIndex }))
	}

	const pagedItems = React.useMemo(() =>
	{
		const startIndex = reportRequest.pageIndex * reportRequest.pageSize
		return pagingResult.pageCollection.slice(startIndex, startIndex + reportRequest.pageSize)
	}, [pagingResult.pageCollection, reportRequest.pageIndex, reportRequest.pageSize])
	
	const handleReportRequestPropertyChange = (name, value) =>
	{
		setReportRequest((previousReportRequest) =>
		{
			let currentReportRequest = { ...previousReportRequest }
			currentReportRequest[name] = value
			
			return currentReportRequest
		})
	}
	
	const handleDownloadReport = () =>
	{
		ReportingService.downloadReportData(pagingResult.pageCollection, 'ListingReport')
	}
	
	return (
		<>
			<SpinnerComponent
				loading={ isBusy }
				position="global"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
			/>
			<PageTitle
				heading={ t('Reports.ListingReport.Title') }
				subheading=""
				icon="pe-7s-news-paper icon-gradient bg-premium-dark"
			/>
			<Container className="mt--6" fluid>
				<Row>
					<div className="col">
						<Card>
							<CardHeader>
								<div style={{ marginLeft: 'auto' }}>
									<table style={{ marginRight:'10px', marginBottom:'10px' }}>
										<tbody>
											<tr>
												<td>&nbsp;</td>
												<td style={{ paddingRight:'5px', fontSize: 'smaller' }}>
													<Label style={{ marginBottom: '0' }}>From</Label>
												</td>
												<td style={{ paddingRight:'5px' }}>
													<DatePicker
														selected={ reportRequest.reportStartDate }
														onChange={ (date) => handleReportRequestPropertyChange('reportStartDate', date) }
													/>
												</td>
												<td style={{ paddingRight:'5px', fontSize: 'smaller' }}>
													<Label style={{ marginBottom: '0' }}>To</Label>
												</td>
												<td style={{ paddingRight:'5px' }}>
													<DatePicker
														selected={ reportRequest.reportEndDate }
														onChange={ (date) => handleReportRequestPropertyChange('reportEndDate', date) }
													/>
												</td>
												<td style={{ whiteSpace: 'nowrap' }}>
													<Button
														color="secondary"
														size="sm"
														onClick={ () => handleGetReport() }
													>
														{ t('Reports.ListingReport.Get_Report') }
													</Button>
												</td>
												<td style={{ whiteSpace: 'nowrap' }}>
													<Button
														color="secondary"
														size="sm"
														onClick={ () => handleDownloadReport() }
													>
														{ t('Reports.Download') }
													</Button>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</CardHeader>
							<div className="table-responsive">
								<Table className="align-items-center table-flush">
									<thead className="thead-light">
										<tr>
											<th className="sort" data-sort="Status" scope="col">{ t('Reports.ListingReport.Status') }</th>
											<th className="sort" data-sort="Year" scope="col">{ t('Reports.ListingReport.Year') }</th>
											<th className="sort" data-sort="Make" scope="col">{ t('Reports.ListingReport.Make') }</th>
											<th className="sort" data-sort="Model" scope="col">{ t('Reports.ListingReport.Model') }</th>
											<th className="sort" data-sort="Trim" scope="col">{ t('Reports.ListingReport.Trim') }</th>
											<th className="sort" data-sort="Vin" scope="col">{ t('Reports.ListingReport.Vin') }</th>
											<th className="sort" data-sort="Lease_Number" scope="col">{ t('Reports.ListingReport.Lease_Number') }</th>
											<th className="sort" data-sort="Oem_Bank" scope="col">{ t('Reports.ListingReport.Oem_Bank') }</th>
											<th className="sort" data-sort="Location" scope="col">{ t('Reports.ListingReport.Location') }</th>
											<th className="sort" data-sort="Listing_Date" scope="col">{ t('Reports.ListingReport.Listing_Date') }</th>
											<th className="sort" data-sort="Listing_Time" scope="col">{ t('Reports.ListingReport.Listing_Time') }</th>
											<th className="sort" data-sort="Is_Relisting" scope="col">{ t('Reports.ListingReport.Is_Relisting') }</th>
											<th className="sort" data-sort="Listing_Voided" scope="col">{ t('Reports.ListingReport.Listing_Voided') }</th>
											<th className="sort" data-sort="Void_Date" scope="col">{ t('Reports.ListingReport.Void_Date') }</th>
											<th className="sort" data-sort="Void_User_Id" scope="col">{ t('Reports.ListingReport.Void_User_Id') }</th>
										</tr>
									</thead>
									<tbody className="list">
										{pagedItems.map((pageItem, idx) => (
											<tr key={ idx }>
												<td>{ pageItem.status }</td>
												<td>{ pageItem.year }</td>
												<td>{ pageItem.make }</td>
												<td>{ pageItem.model }</td>
												<td>{ pageItem.trim }</td>
												<td>{ pageItem.serialNumber }</td>
												<td>{ pageItem.leaseNumber }</td>
												<td>{ pageItem.oemBank }</td>
												<td>{ pageItem.location }</td>
												<td style={{ whiteSpace: 'nowrap' }}>{ pageItem.listingDate ? dayjs(pageItem.listingDate).format('MMM D, YYYY') : '' }</td>
												<td style={{ whiteSpace: 'nowrap' }}>{ pageItem.listingDate ? dayjs(pageItem.listingDate).format('HH:mm') : '' }</td>
												<td>{ pageItem.isRelisting }</td>
												<td>{ pageItem.listingVoided }</td>
												<td style={{ whiteSpace: 'nowrap' }}>{ pageItem.voidDate ? dayjs(pageItem.voidDate).format('MMM D, YYYY HH:mm') : '' }</td>
												<td>{ pageItem.voidUserId }</td>
											</tr>
										))}
									</tbody>
								</Table>
							</div>
							<CardFooter className="py-4">
								{
									(pagingResult.pageCollection.length === 0) && (
										<Row style={{ marginBottom: '10px', width: '100%' }}>
											<Col md="12" style={{ textAlign: 'center' }}>
												<Label>{t('Reports.ListingReport.No_Results')}</Label>
											</Col>
										</Row>
									)
								}
								<nav aria-label="...">
									{
										(pagingResult.pageCollection.length > 0) && (
											<Paginator
												currentPage={ reportRequest.pageIndex }
												pages={ parseInt(((pagingResult.totalRecords - 1) / reportRequest.pageSize) + 1, 10) }
												selectPage={ handlePageChange }
											/>
										)
									}
								</nav>
							</CardFooter>
						</Card>
					</div>
				</Row>
			</Container>
		</>
	)
}

export default ListingReport
